import Notifications from '@/components/Notifications/Notifications.vue';
import emitter from '@/eventBus';
import { hasUnreadChangelogItems } from '@/services/ChangelogService';

import { makeLogger } from '@/services/utils/logger';
import { LOGGING, SERVICE_BRAND } from '@/config';
import { useClubStore } from '@/stores/club';
import { smallestImageUrl, imageSrcset } from '@/services/utils/imageVariants';
const DEBUG_LOG = false;
const log = makeLogger('Navbar', DEBUG_LOG, LOGGING.AUTH_LOG);


export default {
  components: {
    Notifications
  },
  data() {
    return {
      isShrunk: false,
      clubStore: useClubStore(),
      changelogHasUnread: false,
      isNotificationsPanelOpen: false,
      notificationsPanel: null,
    };
  },
  mounted() {
    this.syncChangelogUnread();
    window.addEventListener('scroll', this.handleScroll);
    this.$watch('$route', () => {
      this.closeNavbar();
    });
    this._onOutsideClick = (e) => {
      const navbarEl = document.getElementById('mainNavbar');
      if (!navbarEl) return;
      const isOpen = navbarEl.classList.contains('show');
      if (!isOpen) return;

      const toggler = document.querySelector('.navbar-toggler, .navbar-v2__toggler, [data-bs-target="#mainNavbar"]');
      const clickedInside = navbarEl.contains(e.target)
        || (toggler && toggler.contains(e.target))
        || (e.target.closest && (e.target.closest('[data-bs-target="#userMenuMobile"]') || e.target.closest('[data-bs-target="#createMenuMobile"]')));

      if (!clickedInside) {
        const instant = e.type !== 'click'; // touchstart/pointerdown → закрываем без анимации
        this.closeNavbar(instant);
      }
    };
    document.addEventListener('click', this._onOutsideClick, true);
    document.addEventListener('touchstart', this._onOutsideClick, true);
    document.addEventListener('pointerdown', this._onOutsideClick, true);

    // Scoped swipe-to-close (prevents page scroll when swiping inside open navbar)
    this._swipeActive = false;
    this._swipeStartY = 0;

    this._onSwipeStart = (e) => {
      const navbarEl = document.getElementById('mainNavbar');
      if (!navbarEl || !navbarEl.classList.contains('show')) return;

      const touch = e.touches && e.touches[0];
      if (!touch) return;

      // Активируем только если жест начался внутри открытого меню
      if (navbarEl.contains(e.target)) {
        this._swipeActive = true;
        this._swipeStartY = touch.clientY;
        // Блокируем прокрутку страницы только на время жеста внутри меню
        document.body.classList.add('no-scroll');
        // Подписываемся на move непассивно, чтобы можно было preventDefault
        document.addEventListener('touchmove', this._onSwipeMove, { passive: false, capture: true });
      }
    };

    this._onSwipeMove = (e) => {
      if (!this._swipeActive) return;
      const touch = e.touches && e.touches[0];
      if (!touch) return;
      // Пока свайп активен внутри меню — полностью блокируем вертикальную прокрутку страницы
      e.preventDefault();
    };

    this._onSwipeEnd = (e) => {
      if (!this._swipeActive) return;
      const navbarEl = document.getElementById('mainNavbar');
      const changed = e.changedTouches && e.changedTouches[0];
      const endY = changed ? changed.clientY : this._swipeStartY;
      const swipeDistance = this._swipeStartY - endY;

      // Завершаем сессию свайпа
      this._swipeActive = false;
      document.removeEventListener('touchmove', this._onSwipeMove, { capture: true });

      // Возвращаем прокрутку страницы
      document.body.classList.remove('no-scroll');

      // Если жест вверх достаточно длинный — закрываем меню
      if (navbarEl && navbarEl.classList.contains('show') && swipeDistance > 20) {
        this.closeNavbar();
        // Предотвращаем инерционную прокрутку после закрытия
        e.preventDefault();
      }
    };

    document.addEventListener('touchstart', this._onSwipeStart, { passive: false, capture: true });
    document.addEventListener('touchend', this._onSwipeEnd, { passive: false, capture: true });
    document.addEventListener('touchcancel', this._onSwipeEnd, { passive: false, capture: true });

    this.$nextTick(() => {
      const el = this.$refs.notificationsOffcanvas;
      const Offcanvas = window.bootstrap?.Offcanvas;
      if (!el || !Offcanvas || !el.classList?.contains('offcanvas')) return;

      this.notificationsPanel = Offcanvas.getOrCreateInstance(el, {
        scroll: false,
        backdrop: true,
      });
      el.addEventListener('hidden.bs.offcanvas', this.handleNotificationsHidden);
    });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this._onOutsideClick) {
      document.removeEventListener('click', this._onOutsideClick, true);
      document.removeEventListener('touchstart', this._onOutsideClick, true);
      document.removeEventListener('pointerdown', this._onOutsideClick, true);
      this._onOutsideClick = null;
    }
    document.removeEventListener('touchstart', this._onSwipeStart, { capture: true });
    document.removeEventListener('touchend', this._onSwipeEnd, { capture: true });
    document.removeEventListener('touchcancel', this._onSwipeEnd, { capture: true });
    document.removeEventListener('touchmove', this._onSwipeMove, { capture: true });
    this.$refs.notificationsOffcanvas?.removeEventListener('hidden.bs.offcanvas', this.handleNotificationsHidden);
    this.notificationsPanel = null;
    this.isNotificationsPanelOpen = false;
    this._swipeActive = false;
  },
  computed: {
    /**
     * Проверяет, авторизован ли пользователь.
     * @returns {boolean}
     */
    isLoggedIn() {
      const value = !!(this.$user?.isAuthenticated?.value);
      log('isLoggedIn', 'isLoggedIn =', value);
      return value;
    },
    /**
     * Получает текущего пользователя.
     * @returns {Object|null}
     */
    currentUser() {
      return this.$user;
    },
    /**
     * В клубном режиме, когда URL относится к успешно загруженному клубу.
     * @returns {boolean}
     */
    isClubMode() {
      return !!this.$route?.params?.clubSlug && !!this.clubStore?.currentClub;
    },

    /**
     * true, если мы находимся в клубе пользователя (slug из URL совпадает с user.clubSlug).
     */
    isOwnClub() {
      if (!this.isClubMode) return false;
      const routeSlug = this.$route?.params?.clubSlug || null;
      const userSlug = this.currentUser?.clubSlug || null;
      return !!routeSlug && !!userSlug && routeSlug === userSlug;
    },

    /**
     * Можно ли показывать меню "Создать".
     */
    canShowCreateMenu() {
      return this.isLoggedIn && this.isOwnClub;
    },

    /**
     * Возвращает данные бренда для шапки.
     * ВНЕ клуба — бренд сервиса из конфига
     * ВНУТРИ клуба — бренд из clubStore (без запросов отсюда)
     */
    brand() {
      // ВНЕ клуба — бренд сервиса из конфига
      const serviceBrand = SERVICE_BRAND;
      if (!this.isClubMode) return serviceBrand;

      // ВНУТРИ клуба — бренд из clubStore (без запросов отсюда)
      const club = this.clubStore?.currentClub || null;
      if (!club) return serviceBrand;

      const short = club?.design?.short || null;
      const full = club?.design?.full || null;

      const hasParts = (v) => !!(v && (v.left || v.highlight || v.right));
      const hasShort = hasParts(short);
      const hasFull = hasParts(full);

      // Если дизайн-имена не заданы, показываем в full реальное название клуба
      const clubName = club?.name || null;
      const fullFallback = clubName
        ? { left: clubName, highlight: '', right: '' }
        : serviceBrand.full;

      const mainColor = club?.design?.mainColor || serviceBrand?.mainColor;
      const highlightColor = club?.design?.highlightColor || serviceBrand?.highlightColor;

      return {
        logo: smallestImageUrl(club?.images?.logo || club?.logoUrl) || serviceBrand.logo,
        logoSrcset: imageSrcset(club?.images?.logo),
        short: hasShort ? short : fullFallback,
        full: hasFull ? full : fullFallback,
        mainColor,
        highlightColor
      };
    },

    brandShort() {
      return this.brand.short;
    },

    brandFull() {
      return this.brand.full;
    },

    brandLogo() {
      return this.brand.logo;
    },


  },
  methods: {
    /**
     * Возвращает clubSlug из выбранного источника.
     * scope:
     *  - 'route' → slug из URL
     *  - 'user'  → slug из currentUser
     *  - 'auto'  → сначала route, затем user
     *  - 'none'  → без slug
     * @param {'route'|'user'|'auto'|'none'} scope
     * @returns {string|null}
     */
    getClubSlug(scope = 'auto') {
      if (scope === 'none') return null;

      const routeSlug = this.$route?.params?.clubSlug || null;
      const userSlug = this.currentUser?.clubSlug || null;

      if (scope === 'route') return routeSlug;
      if (scope === 'user') return userSlug;

      // auto
      return routeSlug || userSlug || null;
    },

    /**
     * Универсальный строитель ссылок (Route Location) для навбара.
     * @param {string} name имя маршрута
     * @param {{ scope?: 'route'|'user'|'auto'|'none', fallback?: any }} options
     * @returns {Object|null}
     */
    buildLinkTo(name, options = {}) {
      const { scope = 'auto', fallback = { name: 'AllClubs' } } = options || {};

      // Псевдо-маршрут для клика по бренду: вне клуба ведём на список клубов,
      // внутри клуба — на домашнюю страницу клуба из URL.
      if (name === 'BrandHome') {
        return this.isClubMode
          ? this.buildLinkTo('ClubHome', { scope: 'route', fallback })
          : this.buildLinkTo('AllClubs', { scope: 'none', fallback });
      }

      // Маршруты без клубного контекста
      if (scope === 'none') {
        return { name };
      }

      // Маршруты, которые никогда не должны получать clubSlug в params
      // (иначе Vue Router ругается: Discarded invalid param(s) "clubSlug").
      const ROUTES_WITHOUT_CLUB = new Set(['AllClubs', 'Home', 'Features', 'Pricing', 'About', 'Chess']);
      if (ROUTES_WITHOUT_CLUB.has(name)) {
        return { name };
      }

      const clubSlug = this.getClubSlug(scope);
      log('buildLinkTo', 'compute', { name, scope, clubSlug });

      // Если нужен slug, но его нет
      if (!clubSlug) {
        return fallback;
      }

      return {
        name,
        params: { clubSlug },
      };
    },
    /**
    * shrink после 50px
    */
    handleScroll() {
      this.isShrunk = window.scrollY > 20; // 
    },
    /**
     * Вызывает выход пользователя и перенаправляет на главную страницу.
     */
    logout() {
      this.$user.logout();
      this.$router.push({ name: 'AllClubs' });
    },
    /**
     * Открывает модальное окно входа в систему через глобальный эмиттер.
     */
    openLoginModal() {
      log('openLoginModal', 'эмит открытия диалога');
      emitter.emit('open-login-dialog');
    },
    /**
     * Открывает панель уведомлений.
     */
    openChangelogPanel() {
      const el = this.$refs.notificationsOffcanvas;
      if (!el?.classList?.contains('offcanvas')) {
        if (this.isNotificationsPanelOpen) {
          this.closeChangelogPanel();
          return;
        }
        this.isNotificationsPanelOpen = true;
        return;
      }

      if (this.notificationsPanel) {
        this.notificationsPanel.show();
        return;
      }

      const Offcanvas = window.bootstrap?.Offcanvas;
      if (!el || !Offcanvas) return;

      this.notificationsPanel = Offcanvas.getOrCreateInstance(el, {
        scroll: false,
        backdrop: true,
      });
      this.notificationsPanel.show();
    },
    /**
     * Закрывает кастомную панель уведомлений и отмечает видимые уведомления прочитанными.
     */
    closeChangelogPanel() {
      this.isNotificationsPanelOpen = false;
      this.handleNotificationsHidden();
    },
    /**
     * Запоминает, есть ли непрочитанные пункты changelog.
     * @param {boolean} value
     */
    setChangelogUnread(value) {
      this.changelogHasUnread = !!value;
    },
    /**
     * Считает непрочитанные изменения до монтирования панели уведомлений.
     */
    syncChangelogUnread() {
      this.changelogHasUnread = hasUnreadChangelogItems(this.$user);
    },
    /**
     * Обрабатывает закрытие панели уведомлений.
     */
    handleNotificationsHidden() {
      this.$refs.notifications?.markVisibleAsRead?.();
    },
    /**
     * Закрывает навбар, если он открыт. instant=true — без анимации (для touch вне меню).
     * @param {boolean} instant
     */
    closeNavbar(instant = false) {
      const navbarEl = document.getElementById('mainNavbar');
      if (!navbarEl) return;

      const C = window.bootstrap && window.bootstrap.Collapse;
      if (C) {
        const inst = C.getOrCreateInstance(navbarEl, { toggle: false });
        if (instant) {
          // Попытка минимизировать визуальную задержку: дважды вызвать hide()
          // (Bootstrap сам синхронизирует состояние; цель — закрыть максимально быстро)
          inst.hide();
          inst.hide();
        } else {
          inst.hide();
        }
      } else {
        navbarEl.classList.remove('show');
      }
    },
  }
};
